/**
 * CompactConnectionItem - Standardized compact connection list item
 *
 * A reusable list item component for displaying connections in a compact format.
 * Used in ConnectionTab, ConnectionPicker, and ConnectionApprovalCard.
 */

import { useCallback, forwardRef } from 'react';
import type { CSSProperties } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  PlugConnected16Regular,
  Sparkle16Filled,
} from '@fluentui/react-icons';
import { CompactStatusDot } from './CompactStatusDot';
import type { CompactStatus } from '@/types/connectionStatus';

/**
 * Rendered pixel height of a single CompactConnectionItem row. This is the
 * single source of truth for virtualized lists (react-window) that must
 * absolutely-position rows and therefore cannot measure intrinsic height.
 *
 * Box model: 24px icon + 2×`spacingVerticalXS` (4px) padding + 2×1px border
 * ≈ 30px of content, plus ~6px so the row visually breathes the way the plain
 * (non-virtualized) list's inter-row gap does. Keep this in sync with the
 * `item` style below if its padding/border/icon size ever changes.
 */
export const COMPACT_CONNECTION_ITEM_HEIGHT = 36;

const useStyles = makeStyles({
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  itemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  itemDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  iconContainer: {
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconContainerAI: {
    backgroundColor: tokens.colorPaletteTealBackground2,
  },
  iconContainerAIFoundry: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
  },
  icon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  iconAI: {
    color: tokens.colorPaletteTealForeground2,
  },
  iconAIFoundry: {
    color: tokens.colorPalettePurpleForeground2,
  },
  iconImage: {
    width: '16px',
    height: '16px',
    objectFit: 'contain',
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  name: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  referenceKey: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 1,
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
});

export interface CompactConnectionItemProps {
  /** Connection display name */
  name: string;
  /** Connection reference key */
  referenceKey: string;
  /** Icon URL (optional) */
  iconUri?: string;
  /** Whether this is an AI/Agent connection */
  isAI?: boolean;
  /**
   * Sub-variant used when `isAI` is true. Controls the icon tint so users can
   * distinguish native (Azure OpenAI / OpenAI) agents from Foundry agents at
   * a glance. Defaults to `'native'`.
   */
  aiVariant?: 'native' | 'foundry';
  /** Connection status */
  status?: CompactStatus;
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Whether this item is disabled */
  disabled?: boolean;
  /** Selection callback */
  onSelect?: (referenceKey: string) => void;
  /** Whether to show the reference key */
  showReferenceKey?: boolean;
  /** Additional CSS class */
  className?: string;
  /**
   * Root element id. Set when the item participates in an
   * `aria-activedescendant` listbox so the container can reference the active
   * option.
   */
  id?: string;
  /**
   * Inline style for the root element. Required by virtualized lists
   * (react-window) which absolutely-position each row.
   */
  style?: CSSProperties;
  /**
   * Whether the row is itself keyboard/mouse focusable. Defaults to `true`
   * (a `tabIndex` of `0`, or `-1` when disabled). Pass `false` when the item
   * lives inside an `aria-activedescendant` listbox: the container holds the
   * single tab stop and moves a virtual cursor, so the options must NOT be
   * focus targets — omitting `tabIndex` entirely also prevents a mouse click
   * from stealing focus off the listbox (a `tabIndex={-1}` element is still
   * mouse-focusable, which would silently break arrow-key navigation).
   */
  focusable?: boolean;
}

/**
 * CompactConnectionItem - Standardized connection list item
 *
 * `forwardRef` is used so a parent listbox can keep a ref to each option's
 * root and call `scrollIntoView` on keyboard-driven `aria-activedescendant`
 * transitions (Home/End/PageUp/PageDown/page transitions) — the listbox
 * container is the single tab stop, so scroll needs the option's DOM node.
 */
export const CompactConnectionItem = forwardRef<HTMLDivElement, CompactConnectionItemProps>(function CompactConnectionItem({
  name,
  referenceKey,
  iconUri,
  isAI = false,
  aiVariant = 'native',
  status = 'connected',
  isSelected = false,
  disabled = false,
  onSelect,
  showReferenceKey = false,
  className,
  id,
  style,
  focusable = true,
}, ref) {
  const styles = useStyles();

  const handleClick = useCallback(() => {
    if (!disabled && onSelect) {
      onSelect(referenceKey);
    }
  }, [disabled, onSelect, referenceKey]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && onSelect) {
        e.preventDefault();
        onSelect(referenceKey);
      }
    },
    [disabled, onSelect, referenceKey]
  );

  const itemClassName = mergeClasses(
    styles.item,
    isSelected && styles.itemSelected,
    disabled && styles.itemDisabled,
    className
  );

  return (
    <div
      ref={ref}
      className={itemClassName}
      id={id}
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="option"
      tabIndex={focusable ? (disabled ? -1 : 0) : undefined}
      aria-selected={isSelected}
      aria-disabled={disabled}
    >
      {/* Icon */}
      <div
        className={mergeClasses(
          styles.iconContainer,
          isAI && (aiVariant === 'foundry' ? styles.iconContainerAIFoundry : styles.iconContainerAI)
        )}
      >
        {iconUri ? (
          <img
            src={iconUri}
            alt=""
            className={styles.iconImage}
            aria-hidden="true"
          />
        ) : isAI ? (
          <Sparkle16Filled
            className={mergeClasses(
              styles.icon,
              aiVariant === 'foundry' ? styles.iconAIFoundry : styles.iconAI
            )}
          />
        ) : (
          <PlugConnected16Regular className={styles.icon} />
        )}
      </div>

      {/* Content */}
      <div className={styles.content}>
        <Text className={styles.name}>{name}</Text>
        {showReferenceKey && referenceKey !== name && (
          <Text className={styles.referenceKey}>{referenceKey}</Text>
        )}
      </div>

      {/* Status */}
      <div className={styles.statusContainer}>
        <CompactStatusDot status={status} size="small" />
      </div>
    </div>
  );
});

CompactConnectionItem.displayName = 'CompactConnectionItem';
